import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  LogOut, 
  Menu, 
  X,
  Home,
  ChevronRight
} from 'lucide-react';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [adminData, setAdminData] = useState(null);

  useEffect(() => {
    const data = localStorage.getItem('admin_data');
    if (data) {
      setAdminData(JSON.parse(data));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/properties', icon: Building2, label: 'Properties' },
    { path: '/admin/leads', icon: Users, label: 'Leads' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-dark flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-dark-light border-r border-dark-lighter transition-all duration-300 flex flex-col fixed h-full z-40`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-dark-lighter">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <div>
                <h1 className="font-heading text-2xl font-bold text-rose-gold">PROPVANTA</h1>
                <p className="text-white/40 text-xs uppercase tracking-widest mt-1">Admin Portal</p>
              </div>
            ) : (
              <span className="font-heading text-2xl font-bold text-rose-gold">P</span>
            )}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white/60 hover:text-white transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive(item.path)
                  ? 'bg-rose-gold/20 text-rose-gold border border-rose-gold/30'
                  : 'text-white/60 hover:bg-dark hover:text-white'
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-dark-lighter space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/60 hover:bg-dark hover:text-white transition-all"
          >
            <Home size={20} />
            {sidebarOpen && <span className="font-medium">View Website</span>}
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="bg-dark-light border-b border-dark-lighter px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <span>Admin</span>
              <ChevronRight size={16} />
              <span className="text-rose-gold capitalize">
                {location.pathname.split('/').pop() || 'Dashboard'}
              </span>
            </div>
            {adminData && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-rose-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-rose-gold font-semibold text-sm">
                    {adminData.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-white text-sm font-medium">{adminData.name}</p>
                  <p className="text-white/40 text-xs">{adminData.email}</p>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
